import '@forest/native-diagnostics'

import './lib/posthog'
import {
  getProducts,
  getReceiptSystemAccountIdSafe,
  initializeNativeBridge,
  native,
  pageVisibleOnce,
  type Products,
} from '@forest/native-services'
import { defineCustomClientStrategy } from './paraglide/runtime'
import { appVersion, debug, language, platform } from './params'
import { api } from './services/api'
import { $receiptSystemAccountId } from './store/receipt-system-account-id'
import { setProducts } from './store/products'
import { $serviceLevelStatus } from './store/service-level-status'
import { userInfoSource } from './store/user-info'
import type { ServiceLevelStatus } from './types/service-level'

defineCustomClientStrategy('custom-queryParam', {
  getLocale: () => language,
  setLocale: () => {},
})

initializeNativeBridge({
  bifrost: { enableDebug: import.meta.env.MODE !== 'production' },
  onPageVisible: () => pageVisibleOnce.done(),
})

void getReceiptSystemAccountIdSafe(appVersion).then((id) => {
  $receiptSystemAccountId.set(id)
})

const userInfoPromise = userInfoSource.getReady()
const productsPromise = getProducts({
  cacheKeyPrefix: 'trialExpirationPaywall.products.v2',
  api,
  appVersion,
  platform,
  deferredUserInfo: userInfoPromise,
  env: import.meta.env.MODE as 'production' | 'staging' | 'dev',
  shouldMock: import.meta.env.DEV,
})

function productIdsOf(products: Products): string {
  return [
    products.annualOriginal.product_id,
    products.annualEarlyBird.product_id,
    products.monthlyOriginal.product_id,
    products.monthlyEarlyBird.product_id,
  ]
    .concat(products.monthlyNoTrial ? [products.monthlyNoTrial.product_id] : [])
    .sort((a, b) => a - b)
    .join(',')
}

const useDevMock =
  import.meta.env.DEV || (import.meta.env.MODE === 'dev' && debug)

if (useDevMock) {
  const now = Date.now()
  $serviceLevelStatus.set({
    service_level: 'subscription',
    is_premium: true,
    subscription: {
      auto_renew_sku_id: 'dev_auto_renew_sku_id',
      cancel_reason: '',
      cancelled_at: '',
      created_at: new Date(now - 7 * 86_400_000).toISOString(),
      expires_at: new Date(now + 7 * 86_400_000).toISOString(),
      id: 1,
      is_auto_renewing: true,
      is_in_introductory: false,
      is_in_trial: false,
      payment_system_psid: 1,
      promotional_offer_identifier: '',
      receiptable_id: 1,
      sku_id: 'dev_sku_id',
      starts_at: new Date(now - 7 * 86_400_000).toISOString(),
      transaction_id: 'dev_transaction_id',
      user_id: 1,
    },
  })

  productsPromise
    .then((products) => {
      setProducts(products)
      native.general.logEvent({
        event_name: 'plus_price_test_config',
        event_params: { product_ids: productIdsOf(products) },
      })
    })
    .catch(() => {
      native.general.close()
    })
} else {
  ;(async function getData() {
    try {
      const userInfo = await userInfoPromise

      const products = await productsPromise
      setProducts(products)
      native.general.logEvent({
        event_name: 'plus_price_test_config',
        event_params: { product_ids: productIdsOf(products) },
      })

      const serviceLevelStatus = await api.service
        .get(`user/users/${userInfo.user_id}/service-levels/status`)
        .json<ServiceLevelStatus>()

      $serviceLevelStatus.set(serviceLevelStatus)
      pageVisibleOnce.promise.then(() => {
        const expiresAt = new Date(
          serviceLevelStatus.subscription.expires_at,
        ).getTime()
        const diffMs = expiresAt - Date.now()
        const days = Number.isFinite(diffMs)
          ? Math.max(0, Math.ceil(diffMs / 86_400_000))
          : 0
        native.general.logEvent({
          event_name: 'page_view',
          event_params: {
            page_name: `trial_expire_${days}_${serviceLevelStatus.is_premium ? 'pro' : 'free'}_paywall`,
          },
        })
      })
      native.general.pageReady()
    } catch {
      native.general.close()
    }
  })()
}
